import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/** Slide-in panel (right side on desktop, full screen on mobile) where the user
 *  chats with Claude to improve the given text. The parent passes:
 *    - projectId
 *    - sectionLabel (for system prompt)
 *    - value (current text)
 *    - onApply(newText, mode: 'replace'|'append')  -> parent updates the text
 *    - onClose()
 */
export default function AiChatPanel({ projectId, sectionLabel, value, onApply, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const next = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setError("");
    setLoading(true);
    try {
      const r = await api.post(
        `/icaa/projects/${projectId}/ai/chat-improve`,
        { current_text: value || "", messages: next, section_label: sectionLabel || "" },
        { timeout: 180000 },
      );
      setMessages([...next, { role: "assistant", content: r.data.reply || "", suggested: r.data.suggested_text || "" }]);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error de IA");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
  };

  const QUICK = [
    "Hazlo más conciso",
    "Tono más literario",
    "Amplía la descripción visual",
    "Más cinematográfico, menos descriptivo",
    "Revísame estilo y ritmo",
  ];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ai-chat-panel">
      {/* backdrop */}
      <div className="flex-1 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} />
      {/* panel */}
      <aside className="flex h-full w-full flex-col bg-[#FAFAF6] shadow-2xl md:w-[440px] md:max-w-[480px]"
             onClick={(e) => e.stopPropagation()}>
        <header className="flex items-start justify-between gap-3 border-b border-stone-300/60 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26775E]">grabarte · IA</p>
            <h2 className="mt-0.5 font-display text-lg leading-tight">{sectionLabel || "Mejorar texto"}</h2>
            <p className="mt-0.5 text-[11px] italic text-stone-500">Cmd/Ctrl + Enter para enviar</p>
          </div>
          <button onClick={onClose} data-testid="ai-chat-close"
                  className="shrink-0 rounded-full p-1.5 text-stone-500 hover:bg-stone-100 hover:text-stone-900">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>

        {/* current text preview */}
        <details className="border-b border-stone-200 bg-white">
          <summary className="cursor-pointer px-4 py-2 text-[11px] uppercase tracking-widest text-stone-600 hover:bg-stone-50 sm:px-5">
            Ver texto actual ({(value || "").length} caracteres)
          </summary>
          <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap px-4 pb-3 font-serif text-[12px] leading-snug text-stone-700 sm:px-5">
            {value || "(vacío)"}
          </pre>
        </details>

        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {messages.length === 0 && (
            <div className="rounded-lg border border-dashed border-stone-300 bg-white/60 p-4 text-sm text-stone-600">
              <p className="font-medium text-stone-700">Cuéntale a Claude qué quieres cambiar.</p>
              <p className="mt-1 text-xs text-stone-500">
                Ejemplo: <em>"hazlo más conciso, en dos párrafos, conservando la imagen del mar"</em>.
              </p>
              <div className="mt-3 flex flex-wrap gap-1">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => setInput((s) => s ? s + " · " + q : q)}
                          className="rounded-full bg-stone-200 px-3 py-1 text-[11px] text-stone-700 hover:bg-stone-300">
                    + {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`mb-3 ${m.role === "user" ? "flex justify-end" : ""}`} data-testid={`msg-${i}`}>
              {m.role === "user" ? (
                <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-stone-900 px-4 py-2 text-sm text-[#FAFAF6]">
                  {m.content}
                </p>
              ) : (
                <div className="max-w-[92%]">
                  <p className="rounded-2xl rounded-bl-sm bg-white px-4 py-2 text-[13px] leading-relaxed text-stone-800 shadow-sm">
                    {m.content}
                  </p>
                  {m.suggested && (
                    <div className="mt-2 rounded-xl border border-[#26775E]/30 bg-[#26775E]/5 p-3" data-testid={`suggestion-${i}`}>
                      <p className="text-[10px] uppercase tracking-widest text-[#26775E]">Propuesta</p>
                      <pre className="mt-1 whitespace-pre-wrap font-serif text-[13px] leading-relaxed text-stone-800">{m.suggested}</pre>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button onClick={() => { onApply(m.suggested, "replace"); onClose?.(); }}
                                data-testid={`apply-replace-${i}`}
                                className="rounded-full bg-[#26775E] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#1c5c48]">
                          Reemplazar
                        </button>
                        <button onClick={() => onApply(m.suggested, "append")}
                                data-testid={`apply-append-${i}`}
                                className="rounded-full border border-stone-400 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-700 hover:bg-stone-100">
                          Añadir al final
                        </button>
                        <button onClick={() => navigator.clipboard?.writeText(m.suggested)}
                                className="rounded-full px-3 py-1.5 text-[11px] text-stone-500 hover:text-stone-900">
                          Copiar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-[12px] italic text-[#26775E]" data-testid="ai-chat-loading">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#26775E]" />
              Claude está pensando…
            </div>
          )}
          {error && <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="ai-chat-error">{error}</p>}
        </div>

        {/* input */}
        <div className="border-t border-stone-300/60 bg-white p-3 sm:p-4">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
                    data-testid="ai-chat-input"
                    rows={3} placeholder="Indica qué quieres cambiar y la IA te devolverá una versión lista para aplicar."
                    className="w-full resize-none rounded-md border border-stone-300 bg-[#FBFAF4] p-2.5 text-sm outline-none focus:border-[#26775E]" />
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-[10px] text-stone-400">{messages.filter((m) => m.role === "assistant").length} respuestas</p>
            <button onClick={send} disabled={loading || !input.trim()} data-testid="ai-chat-send"
                    className="rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#1c5c48] disabled:opacity-40">
              {loading ? "Pensando…" : "Enviar"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
